/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
// React
import React from 'react';
import { Link } from 'react-router-dom';
// Redux Toolkit
import { useSelector } from 'react-redux';
import { selectUser } from '../../store/userSlice';
import authRoles from '../../auth/authRoles';
// BASE components
import BASESvgIcon from '../../../@BASE/core/BASESvgIcon';
import useThemeMediaQuery from '../../../@BASE/hooks/useThemeMediaQuery';
// Material-UI components
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
// Custom components
import EmptyVersionsCard from '../crud/instance/version/EmptyVersionsCard';

function VersioningCard({ instance, steps }) {
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('sm'));
  const user = useSelector(selectUser);

  if (!instance || !instance.archived_versions) {
    return null;
  }

  return (
    <div className="w-full">
      <div className="mb-32 space-y-16">
        <Typography className="grow text-2xl font-bold tracking-tight leading-none py-8">
          Versions
        </Typography>
        <Divider />
        <Paper className="flex flex-col flex-auto p-24 shadow rounded-xl overflow-hidden">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <Typography className="text-lg font-medium tracking-tight leading-6 truncate">
                Version Control
              </Typography>
              <Typography
                className="text-md font-medium"
                color="text.secondary"
              >
                Stored Lesson Versions
              </Typography>
            </div>
          </div>
          <div className="my-32 space-y-32">
            <div className="flex flex-col">
              {instance.archived_versions.length === 0 ? (
                <EmptyVersionsCard resource="lesson" />
              ) : (
                <List className="py-0 mt-8 divide-y">
                  {instance.archived_versions.map((version, index) => (
                    <ListItem key={index} className="px-0">
                      <ListItemText
                        classes={{ root: 'px-8', primary: 'font-medium' }}
                        primary={version.version}
                        secondary={
                          <span className="flex flex-col sm:flex-row sm:items-center -ml-2 mt-8 sm:mt-4 space-y-4 sm:space-y-0 sm:space-x-12">
                            {version.date && (
                              <span className="flex items-center">
                                <BASESvgIcon size={20} color="disabled">
                                  heroicons-solid:calendar
                                </BASESvgIcon>
                                <Typography
                                  component="span"
                                  className="mx-6 text-md"
                                  color="text.secondary"
                                >
                                  {version.date}
                                </Typography>
                              </span>
                            )}

                            {version.user && (
                              <span className="flex items-center">
                                <BASESvgIcon size={20} color="disabled">
                                  heroicons-solid:user
                                </BASESvgIcon>
                                <Typography
                                  component="span"
                                  className="mx-6 text-md"
                                  color="text.secondary"
                                >
                                  {version.user.first_name}{' '}
                                  {version.user.last_name}
                                </Typography>
                              </span>
                            )}
                          </span>
                        }
                      />
                      {/* <ListItemSecondaryAction>
                        <IconButton aria-label="more">
                          <BASESvgIcon>heroicons-solid:arrow-sm-right</BASESvgIcon>
                        </IconButton>
                      </ListItemSecondaryAction> */}
                    </ListItem>
                  ))}
                </List>
              )}
            </div>
          </div>
          {authRoles.admin.includes(user.role) && (
            <div className="flex justify-end">
              <Button
                to="version/new/edit"
                component={Link}
                variant="outlined"
                endIcon={
                  <BASESvgIcon className="" size={20}>
                    heroicons-solid:plus
                  </BASESvgIcon>
                }
              >
                Add Version
              </Button>
            </div>
          )}
        </Paper>
      </div>
    </div>
  );
}

export default VersioningCard;
